// react
import { useState, Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  DialogActions,
  Button,
  useTheme,
  styled,
  DialogContentText,
  Typography,
  IconButton,
  Popover,
  PopoverOrigin,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
// src
import { config } from "../config/config";
import { pjNameValidationCheck } from "./Utility/ValidationUtil";
import { LoadingButton } from "@mui/lab";
import { ExpandMore, FolderZip } from "@mui/icons-material";
import { Common } from "../../common/src";
import { unzip } from "unzipit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import Spacer from "./Spacer";
import IOSSwitch from "./IOSSwitch";

type Props = {
  projectID: string;
  projectName: string;
  setProjectName?: Dispatch<SetStateAction<string>>;
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  jobSiteId: string;
  setJobSiteId?: Dispatch<SetStateAction<string>>;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  setProgressDialog: Dispatch<SetStateAction<boolean>>;
  setUploadResult: Dispatch<SetStateAction<boolean>>;
  setUpLoadingPercent: Dispatch<SetStateAction<number>>;
  setIsShortageImageDialog: Dispatch<SetStateAction<boolean>>;
};

const UploadZipDialog = ({
  projectID,
  projectName,
  setProjectName,
  openDialog,
  setOpenDialog,
  jobSiteId,
  setJobSiteId,
  setErrorSnackbar,
  setErrorMsg,
  setOpenWarning,
  setMsgWarning,
  setProgressDialog,
  setUploadResult,
  setUpLoadingPercent,
  setIsShortageImageDialog,
}: Props) => {
  const { t } = useTranslation();
  // Loding flag
  const [whileLoading, setWhileLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [timeStamp, setTimeStamp] = useState<number>(0);
  const [useArmarker, setUseArmarker] = useState<string>("false");
  const [jobsiteList, setJobsiteList] = useState<Common.LandlogJobSite[]>([]);
  const [fileSelected, setFileSelected] = useState(false);
  // const [uploadResult, setUploadResult] = useState(false);
  const [uploadingCheck, setUploadingCheck] = useState(false);
  const [importFileName, setImportFileName] = useState<string>("");
  const [FileSize, setFileSize] = useState<string>("");
  // Importボタン制御
  const [pNameCheck, setPNameCheck] = useState<boolean>(true);
  const [uploadCheck, setUploadCheck] = useState(true);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(true);
  const directoryName = importFileName;
  // dashboard連携
  const [isCoordinateSetting, setIsCoordinateSetting] = useState<boolean>(true);
  const [isLinkDashboard, setIsLinkDashboard] = useState<boolean>(false);
  const [shouldInterpolate, setShouldInterpolate] = useState<boolean>(true);

  useEffect(() => {
    const handleFocus = () => {
      if (!openDialog) {
        document.body.style.cssText = "";
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [openDialog]);

  useEffect(() => {
    //インポート可否チェック
    if (fileSelected && jobSiteId && projectName && isValid) {
      setUploadCheck(false);
      setPNameCheck(false);
    } else {
      setUploadCheck(true);
      setPNameCheck(true);
    }
  }, [fileSelected, jobSiteId, projectName, isValid]);

  useEffect(() => {
    if (openDialog) {
      handleOpen();
    }
  }, [openDialog]);

  const handleOpen = async () => {
    const apiUrl = `${config.serverHost}/api/v1/user/setting`;

    const fetchData = async () => {
      try {
        const res = await axios.get(apiUrl, { withCredentials: true });
        setIsCoordinateSetting(res.data.coordinateSetting);
        setIsLinkDashboard(res.data.linkDashboard);
      } catch (e) {
        console.error(e);
      }
    };

    await fetchData();
    await getJobSite();
    setIsValid(true);
    setShouldInterpolate(true);
    // document.body.style.cssText = `position: fixed; top: -${window.scrollY}px; overflow-y: scroll; width: 100%;`;
  };

  const handleClose = () => {
    setOpenDialog(false);
    setImportFileName("");
    setFileSize("");
    setFileSelected(false);
    const scrollY = document.body.style.top;
    document.body.style.cssText = "";
    window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
  };

  const getJobSite = async () => {
    let apiUrl = `${config.serverHost}/api/v1/sites`;
    axios
      .get(apiUrl, {
        withCredentials: true,
      })
      .then((res: AxiosResponse<Common.LandlogJobsitesInfo>) => {
        setJobsiteList(res.data.sites);
      });
  };

  const getFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files.length === 0) return;
    setLoadingFile(true);
    const file: File = e.target.files[0];

    const kb = 1024;
    const mb = Math.pow(kb, 2);
    const gb = Math.pow(kb, 3);

    let target = null;
    let unit = "byte";

    if (file.size >= gb) {
      target = gb;
      unit = "GB";
    } else if (file.size >= mb) {
      target = mb;
      unit = "MB";
    } else if (file.size >= kb) {
      target = kb;
      unit = "KB";
    }

    const res =
      target !== null
        ? Math.floor((file.size / target) * 100) / 100
        : file.size;
    const sizeUnit = res + unit;

    try {
      const { entries } = await unzip(file);
      Object.entries(entries).forEach(async ([name, entry]) => {
        if (name.includes("catchLog.ndjson")) {
          const data = await entry.text();
          const lines = data.split("\n");
          lines.map((line) => {
            const json = JSON.parse(line);
            if (json.init) {
              setTimeStamp(Number(json.init.time) * 1000);
            }
          });
        }
      });

      setFile(file);
      setImportFileName(file.name);
      setFileSize(sizeUnit);
      if (e.target.files[0].name !== "" && e.target.files[0].size !== 0) {
        setFileSelected(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingFile(false);
    }
  };

  const uploadPFile = async () => {
    setProgressDialog(true);
    setOpenDialog(false);
    setImportFileName("");
    setFileSize("");
    setFileSelected(false);
    setWhileLoading(true);
    try {
      if (file) {
        const data = new FormData();
        data.append("uploadFile", file);
        data.append("projectName", projectName);
        data.append("jobsiteId", jobSiteId);
        data.append("directory", directoryName);
        data.append("coordinateSetting", isCoordinateSetting ? "1" : "0");
        data.append("linkDashboard", isLinkDashboard ? "1" : "0");
        data.append("shouldInterpolate", shouldInterpolate ? "1" : "0");

        let d = new Date();
        if (timeStamp !== 0) {
          d = new Date(timeStamp);
          data.append("timeStamp", d.toISOString());
        }
        data.append("useArmarker", useArmarker);
        const timezone: number = new Date().getTimezoneOffset() * -1;
        data.append("timezone", `${timezone}`);

        await axios
          .post(
            `${config.serverHost}/api/v1/project/${projectID}/ar/zip`,
            data,
            {
              withCredentials: true,
              onUploadProgress: (progressEvent) => {
                let percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setUpLoadingPercent(percent);
              },
            }
          )
          .then((res) => {
            if (res.data.isShortageImage) {
              setIsShortageImageDialog(true);
              setUpLoadingPercent(0);
            } else {
              setUploadResult(true);
            }
            setProgressDialog(false);
          })
          .catch((err) => {
            if (err.response.status === 504) {
              setOpenWarning(true);
              setMsgWarning(t("SERVER_TIME_OUT"));
            } else {
              setErrorSnackbar(true);
              setErrorMsg(t("UPLOAD_FAILED"));
            }
            setWhileLoading(false);
            setProgressDialog(false);
          });
      } else {
        setErrorSnackbar(true);
        setWhileLoading(false);
        setProgressDialog(false);
        setErrorMsg(t("FILE_NOT_EXISTS"));
      }
    } catch (err) {
      setErrorSnackbar(true);
      setWhileLoading(false);
      setProgressDialog(false);
      setErrorMsg(t("UPLOAD_FAILED"));
    }
  };

  const onChangeHandle = (e: string) => {
    setProjectName(e);

    setIsValid(pjNameValidationCheck(e));
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  const customMainColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
  };

  const customAccordionBackground = {
    background: theme.palette.base.modal,
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  type CustomPopoverProps = {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
    content: string;
  };
  const CustomPopover = ({
    anchorOrigin,
    transformOrigin,
    content,
  }: CustomPopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const replaceNewLine = (s: String) => {
      return s.split("\n").map((line) => (
        <span>
          {line}
          <br />
        </span>
      ));
    };

    const open = Boolean(anchorEl);

    return (
      <div>
        <IconButton onClick={handleClick} style={{ padding: 0 }}>
          <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <Typography sx={{ p: 2 }} style={{ width: "600px" }}>
            {content ? replaceNewLine(content) : ""}
          </Typography>
        </Popover>
      </div>
    );
  };

  return (
    <>
      <DialogContentText style={customColor} className="upload-file7">
        {t("SELECT_ZIP_FILE")}
      </DialogContentText>
      {fileSelected ? (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <LoadingButton
            style={{
              ...customMainColor,
              boxShadow: "none",
              marginTop: "10px",
              marginBottom: "15px",
            }}
            size="small"
            variant="contained"
            component="label"
            className="loading-white upload-file8"
            loading={loadingFile}
          >
            {t("FILE_CHANGE")}
            <input type="file" accept=".zip" onChange={getFile} hidden />
          </LoadingButton>
          <DialogContentText
            style={customColor}
            className="upload-file7 upload-file10"
          >
            <FolderZip
              style={customColor}
              sx={{ width: "60px", height: "60px" }}
            />
            <div id="upload-file11">
              <p id="upload-file12">{importFileName}</p>
              <p id="upload-file13">{FileSize}</p>
            </div>
          </DialogContentText>
        </div>
      ) : (
        <>
          <LoadingButton
            style={{
              ...customMainColor,
              boxShadow: "none",
              marginTop: "10px",
              marginBottom: "15px",
            }}
            size="small"
            variant="contained"
            component="label"
            className="loading-white upload-file6"
            loading={loadingFile}
          >
            {t("FILE_SELECT")}
            <input type="file" accept=".zip" onChange={getFile} hidden />
          </LoadingButton>
        </>
      )}
      <div className="setting-select1" style={{ marginLeft: 10 }}>
        <div className="setting-text-left">{t("ALIGN_SMART_DASHBOARD")}</div>
        <div className="setting-text-right">
          <CustomPopover
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            content={
              t("NOTE_SETTING") +
              "\n" +
              t("ONLY_FOLLOWING_CASES") +
              "\n" +
              "・" +
              t("WHEN_SETTING_DASHBOARD_COORDINATE_SYSTEM") +
              "\n" +
              "・" +
              t("WHEN_SHOOTING_VIDOC")
            }
          />
          <Spacer height={0} width={10} />
          <IOSSwitch
            onChange={(e) => {
              setIsCoordinateSetting(e.target.checked);
              if (!e.target.checked) {
                // 座標系連携がoffならDashboard連携もoff
                setIsLinkDashboard(false);
              }
            }}
            checked={isCoordinateSetting}
          />
        </div>
      </div>

      <div className="setting-select1" style={{ marginLeft: 10 }}>
        <div className="setting-text-left">{t("UPLOAD_SYSTEM_SETTING")}</div>
        <div className="setting-text-right">
          <CustomPopover
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            content={
              t("UPLOAD_SETTING") +
              "\n" +
              t("ONLY_FOLLOWING_CASES") +
              "\n" +
              "・" +
              t("WHEN_ALIGN_TO_DASHBOARD_COORDINATE_SYSTEM")
            }
          />
          <Spacer height={0} width={10} />
          <IOSSwitch
            onChange={(e) => {
              setIsLinkDashboard(e.target.checked);
              if (e.target.checked) {
                // Dashboard連携がonなら座標系連携もon
                setIsCoordinateSetting(true);
              }
            }}
            checked={isLinkDashboard}
          />
        </div>
      </div>

      <Accordion
        disableGutters={true}
        sx={{
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          ".MuiAccordionSummary-root": { justifyContent: "start" },
          ...customAccordionBackground,
        }}
      >
        <AccordionSummary
          sx={{
            minHeight: 30,
            height: 30,
            padding: 0,
            ".MuiAccordionSummary-content": { flexGrow: 0 },
          }}
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          {"【" + t("ADVANCED_SETTINGS") + "】"}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <div
            className="setting-select1"
            style={{ marginTop: 0, marginLeft: 10 }}
          >
            <div className="setting-text-left">
              {t("INTERPOLATE_SETTING_C1")}
            </div>
            <div className="setting-text-right">
              <CustomPopover
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                content={
                  t("INTERPOLATE_SETTING_D1") +
                  "\n" +
                  t("INTERPOLATE_SETTING_D2")
                }
              />
              <Spacer height={0} width={10} />
              <IOSSwitch
                onChange={(e) => {
                  setShouldInterpolate(e.target.checked);
                }}
                checked={shouldInterpolate}
                disabled={!isLinkDashboard}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <DialogActions style={{ marginTop: "10px", ...customBackgroundStyles }}>
        <CustomCanselButton
          size="small"
          variant="contained"
          onClick={handleClose}
          id="upload-file19"
        >
          {t("CANCEL")}
        </CustomCanselButton>
        <LoadingButton
          className={`loading-white upload-check-com ${
            uploadCheck ? "upload-check1" : "upload-check2"
          }`}
          size="small"
          variant="contained"
          disabled={uploadCheck || loadingFile || pNameCheck}
          loading={whileLoading}
          onClick={uploadPFile}
          style={
            uploadCheck
              ? { boxShadow: "none" }
              : { ...customMainColor, boxShadow: "none" }
          }
        >
          {t("UPLOAD")}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default UploadZipDialog;
